import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Building2, Shield, Truck, Cpu, GraduationCap, Filter, Award, ClipboardCheck, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import PageFrame from '../components/PageFrame';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  equipment: Truck,
  insurance: Shield,
  services: Building2,
  technology: Cpu,
  training: GraduationCap
};

const categoryColors = {
  equipment: 'bg-blue-500/20 text-blue-400',
  insurance: 'bg-green-500/20 text-green-400',
  services: 'bg-purple-500/20 text-purple-400',
  technology: 'bg-cyan-500/20 text-cyan-400',
  training: 'bg-orange-500/20 text-orange-400'
};

const levelBadges = {
  platinum: 'bg-gradient-to-r from-gray-300 to-gray-100 text-gray-800',
  gold: 'bg-gradient-to-r from-yellow-500 to-yellow-300 text-yellow-900',
  silver: 'bg-gradient-to-r from-gray-400 to-gray-300 text-gray-800',
  standard: 'bg-gray-700 text-gray-300'
};

const QUIZ_INFO = {
  'general-safety': { name: 'Truck Safety Knowledge Test', questions: 10 },
  'accident-prevention': { name: 'Accident Prevention Quiz', questions: 8 },
  'load-securement': { name: 'Load Securement Safety Quiz', questions: 6 }
};

const Partners = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [quizSponsors, setQuizSponsors] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPartners();
    fetchQuizSponsors();
  }, [selectedCategory]);

  const fetchPartners = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      
      const response = await axios.get(`${API}/api/partners/public?${params.toString()}`);
      setPartners(response.data.partners || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuizSponsors = async () => {
    try {
      // Fetch all quiz sponsors (public endpoint)
      const sponsors = [];
      for (const quizId of Object.keys(QUIZ_INFO)) {
        const response = await axios.get(`${API}/api/admin/quiz-sponsors/${quizId}`);
        if (response.data.sponsor) {
          sponsors.push({ ...response.data.sponsor, quiz_id: quizId });
        }
      }
      setQuizSponsors(sponsors);
    } catch (error) {
      console.error('Failed to fetch quiz sponsors:', error);
    }
  };

  const trackClick = async (partnerId) => {
    try {
      await axios.post(`${API}/api/partners/track-click/${partnerId}`);
    } catch (error) {
      console.error('Failed to track click');
    }
  };

  const handlePartnerClick = (partner) => {
    trackClick(partner.partner_id);
    const url = partner.affiliate_url || partner.website_url;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const featuredPartners = partners.filter(p => p.featured);
  const regularPartners = partners.filter(p => !p.featured);

  return (
    <PageFrame>
      <div className="min-h-screen bg-gray-950">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-transparent" />
          <div className="max-w-6xl mx-auto px-4 relative">
            <div className="text-center mb-12">
              <Badge className="bg-orange-500/20 text-orange-400 mb-4">Trusted Partners</Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Our Industry Partners
              </h1>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                We've partnered with leading companies in the trucking industry to bring you 
                the best resources, services, and solutions for safety and success.
              </p>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-8 border-b border-gray-800">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 text-gray-400">
                <Filter className="h-4 w-4" />
                <span className="text-sm">Filter:</span>
              </div>
              <Button
                variant={selectedCategory === null ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(null)}
                className={selectedCategory === null 
                  ? 'bg-orange-500 hover:bg-orange-600' 
                  : 'border-gray-700 text-gray-300'
                }
              >
                All Partners
              </Button>
              {categories.map((cat) => {
                const Icon = categoryIcons[cat.name] || Building2;
                return (
                  <Button
                    key={cat.name}
                    variant={selectedCategory === cat.name ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(cat.name)}
                    className={selectedCategory === cat.name 
                      ? 'bg-orange-500 hover:bg-orange-600' 
                      : 'border-gray-700 text-gray-300'
                    }
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)} ({cat.count})
                  </Button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Partners Grid */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>
            ) : partners.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="h-16 w-16 mx-auto text-gray-700 mb-4" />
                <h3 className="text-xl text-gray-400">No partners found</h3>
                <p className="text-gray-500">Check back soon for new partnerships!</p>
              </div>
            ) : (
              <>
                {/* Featured Partners */}
                {featuredPartners.length > 0 && !selectedCategory && (
                  <div className="mb-12">
                    <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                      <span className="text-orange-500">★</span> Featured Partners
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      {featuredPartners.map((partner) => {
                        const Icon = categoryIcons[partner.category] || Building2;
                        return (
                          <Card 
                            key={partner.partner_id}
                            className="bg-gradient-to-br from-gray-900 to-gray-800 border-orange-500/30 hover:border-orange-500 transition-all cursor-pointer group"
                            onClick={() => handlePartnerClick(partner)}
                          >
                            <CardContent className="p-6">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex items-center justify-center">
                                    <Icon className="h-6 w-6 text-orange-500" />
                                  </div>
                                  <div>
                                    <h3 className="text-xl font-bold text-white group-hover:text-orange-400 transition-colors">
                                      {partner.name}
                                    </h3>
                                    <Badge className={categoryColors[partner.category] || 'bg-gray-700 text-gray-300'}>
                                      {partner.category}
                                    </Badge>
                                  </div>
                                </div>
                                <Badge className={levelBadges[partner.partnership_level]}>
                                  {partner.partnership_level}
                                </Badge>
                              </div>
                              <p className="text-gray-400 mb-4">{partner.description}</p>
                              <div className="flex items-center text-orange-400 text-sm group-hover:underline">
                                Visit Website <ExternalLink className="h-4 w-4 ml-1" />
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Regular Partners */}
                <div>
                  {!selectedCategory && regularPartners.length > 0 && (
                    <h2 className="text-2xl font-bold text-white mb-6">All Partners</h2>
                  )}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {(selectedCategory ? partners : regularPartners).map((partner) => {
                      const Icon = categoryIcons[partner.category] || Building2;
                      return (
                        <Card 
                          key={partner.partner_id}
                          className="bg-gray-900 border-gray-800 hover:border-gray-700 transition-all cursor-pointer group"
                          onClick={() => handlePartnerClick(partner)}
                        >
                          <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
                                <Icon className="h-5 w-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
                              </div>
                              <div>
                                <h3 className="text-lg font-bold text-white group-hover:text-orange-400 transition-colors">
                                  {partner.name}
                                </h3>
                                <Badge className={`text-xs ${categoryColors[partner.category] || 'bg-gray-700 text-gray-300'}`}>
                                  {partner.category}
                                </Badge>
                              </div>
                            </div>
                            <p className="text-gray-400 text-sm mb-4 line-clamp-3">{partner.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-orange-400 text-sm flex items-center gap-1 group-hover:underline">
                                Learn More <ExternalLink className="h-3 w-3" />
                              </span>
                              {partner.partnership_level !== 'standard' && (
                                <Badge className={`text-xs ${levelBadges[partner.partnership_level]}`}>
                                  {partner.partnership_level}
                                </Badge>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Quiz Sponsors Section */}
        <section className="py-16 bg-gray-900">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <Badge className="bg-orange-500/20 text-orange-400 mb-4">Quiz Sponsorships</Badge>
              <h2 className="text-3xl font-bold text-white mb-4">Safety Quiz Sponsors</h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Our interactive safety quizzes are sponsored by industry leaders committed to truck driver education.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {Object.entries(QUIZ_INFO).map(([quizId, quiz]) => {
                const sponsor = quizSponsors.find(s => s.quiz_id === quizId && s.active);
                return (
                  <Card 
                    key={quizId}
                    className={`bg-gray-800 border-gray-700 ${sponsor ? 'border-orange-500/50' : ''}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                          <ClipboardCheck className="h-5 w-5 text-orange-500" />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">{quiz.name}</h3>
                          <p className="text-gray-500 text-sm">{quiz.questions} questions</p>
                        </div>
                      </div>
                      
                      {sponsor ? (
                        <div className="bg-gradient-to-br from-orange-500/10 to-transparent p-4 rounded-lg border border-orange-500/20">
                          <p className="text-xs text-orange-400 uppercase tracking-wider mb-1">Sponsored by</p>
                          <p className="text-white font-bold mb-2">{sponsor.sponsor_name}</p>
                          {sponsor.banner_url && (
                            <img 
                              src={sponsor.banner_url.startsWith('/') ? `${API}${sponsor.banner_url}` : sponsor.banner_url}
                              alt={sponsor.sponsor_name}
                              className="w-full h-20 object-cover rounded mb-2"
                            />
                          )}
                          {sponsor.click_url && (
                            <a 
                              href={sponsor.click_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-orange-400 text-sm hover:underline flex items-center gap-1"
                            >
                              Visit Sponsor <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      ) : (
                        <div className="bg-gray-700/50 p-4 rounded-lg border border-dashed border-gray-600 text-center">
                          <Award className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                          <p className="text-gray-400 text-sm mb-2">Sponsorship Available</p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                            onClick={() => navigate('/contact?subject=Quiz%20Sponsorship%20Inquiry')}
                          >
                            Inquire Now
                          </Button>
                        </div>
                      )}
                      
                      <Button
                        className="w-full mt-4 bg-gray-700 hover:bg-gray-600 text-white"
                        onClick={() => navigate(`/quiz/${quizId}`)}
                      >
                        Take Quiz <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Sponsor a Quiz CTA */}
            <Card className="bg-gradient-to-r from-orange-500/20 via-orange-500/10 to-transparent border-orange-500/30">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center">
                      <Award className="h-8 w-8 text-orange-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">Sponsor a Safety Quiz</h3>
                      <p className="text-gray-400">
                        Get your brand in front of thousands of truck drivers taking our safety quizzes.
                        Your logo and message will appear in quiz result emails.
                      </p>
                    </div>
                  </div>
                  <Button
                    size="lg"
                    className="bg-orange-500 hover:bg-orange-600 whitespace-nowrap"
                    onClick={() => navigate('/contact?subject=Quiz%20Sponsorship%20Inquiry')}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Inquire About Sponsorship
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Become a Partner CTA */}
        <section className="py-16 bg-gradient-to-br from-orange-500/10 via-gray-900 to-gray-950">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Become a Partner</h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Interested in partnering with TruckAccidentSupport.com? We're always looking to 
              collaborate with companies that share our commitment to trucking safety.
            </p>
            <Button 
              size="lg"
              className="bg-orange-500 hover:bg-orange-600"
              onClick={() => navigate('/contact')}
            >
              Contact Us About Partnership
            </Button>
          </div>
        </section>
      </div>
    </PageFrame>
  );
};

export default Partners;
